// @flow
import { createSelector, createStructuredSelector } from 'reselect';
import { superpathjoin } from 'superpathjoin';

import {
  selectCartBundles,
  selectOneProductById,
  selectOneProductErrors,
  selectProducts,
  selectRawProductsArray,
  type CartBundle,
  type DecoratedProduct,
  type ProductError,
  type ProductId,
  type ProductKind,
  type ProductPayload,
  type ProductPrice,
} from '@gandi/cart';

import { selectAdminURL } from '../../../modules/config';
import type { FullState } from '../../../types';

/**
 * List of ProductId
 */
const EMPTY_ARRAY: Array<ProductId> = [];

export type ByKind<T> = {
  [kind: ProductKind]: T,
};

export type KindProps = {
  productType: string,
  process: string,
};

export type ProductsByKind = ByKind<Array<DecoratedProduct>>;
export type ProductKinds = Array<ProductKind>;
export type ProductIds = Array<ProductId>;
export type CorporateMiscFees = { count: number, priceBeforeTaxes: number };
export type CorporateMiscFeesByKind = ByKind<CorporateMiscFees>;

const productKindGroups = {
  CorporateCreate: 'services',
  TmchCreate: 'services',
  TmchRenew: 'services',
} as const;
type ProductKindGroups = $Values<typeof productKindGroups>;

export const selectProductsByKind: (state: FullState) => ProductsByKind =
  createSelector([selectProducts], (products): ProductsByKind => {
    return products.reduce((acc, product) => {
      let group: ProductKind;
      if (
        product.productType === 'mailboxv2' &&
        product.process === 'renew' &&
        product.payload.fake_renew
      ) {
        group = 'Mailboxv2Convert';
        // $FlowFixMe[incompatible-type] https://gitlab.corp.gandi.net/gandi/squad-shop/app-shop/-/issues/280
      } else if (product.component === 'Mailbox-standaloneCreate') {
        group = 'MailboxStandAloneCreate';
      } else {
        group =
          // $FlowFixMe[prop-missing] all good, we have a fallback just below
          (productKindGroups[product.component] as ProductKindGroups) ||
          product.component;
      }

      if (!acc[group]) {
        acc[group] = [product];
      } else {
        acc[group].push(product);
      }

      return acc;
    }, ({}: ProductsByKind));
  });

export const selectDomainBundles: (state: FullState) => Array<CartBundle> =
  createSelector([selectCartBundles], (cartBundles): Array<CartBundle> => {
    return cartBundles.filter(bundle =>
      bundle.products.every(product => product?.productType === 'domain'),
    );
  });

export const selectProductKinds: (state: FullState) => ProductKinds =
  createSelector([selectProductsByKind], groupped =>
    Object.keys(groupped)
      // CorporateCreate component and other services always goes last
      .sort((a, b) => {
        if (a === b) {
          return 0;
        }
        if (a === 'services') {
          return 1;
        }
        if (b === 'services') {
          return -1;
        }
        if (a.startsWith('Domain') && b.startsWith('Domain')) {
          return 0;
        }
        if (a.startsWith('Domain')) {
          return -1;
        }
        if (b.startsWith('Domain')) {
          return 1;
        }
        return 0;
      }),
  );

export const selectFirstMailboxProductIdsByFqdn: (
  state: FullState,
) => Array<ProductId> = createSelector([selectProductsByKind], groupped => {
  const mailboxes = groupped.Mailboxv2Create;

  if (!mailboxes) return EMPTY_ARRAY;

  const uniqueFqdns = mailboxes
    .map((mailbox: DecoratedProduct) => mailbox.fqdn)
    .filter((e, i, self) => i === self.indexOf(e));

  const firstMailboxes = uniqueFqdns.map(fqdn =>
    mailboxes.find((mailbox: DecoratedProduct) => mailbox.fqdn === fqdn),
  );
  const mailboxIds = firstMailboxes.map(p => p?.id).filter(Boolean);

  return mailboxIds;
});

const selectCorporateMiscFeesFactory: (
  props: KindProps,
) => FullState => CorporateMiscFees = props =>
  createSelector([selectRawProductsArray], products =>
    products
      .filter(
        product =>
          product.productType === props.productType &&
          product.process === props.process,
      )
      .reduce(
        (acc, product) => {
          const miscFeesCorporate =
            (product.options &&
              product.options.find(
                option =>
                  option.description === 'misc_fees' &&
                  option.productType === 'corporate',
              )) ||
            undefined;

          if (!miscFeesCorporate) {
            return acc;
          }

          acc.count += 1;
          acc.priceBeforeTaxes +=
            miscFeesCorporate.unitPrice ||
            // $FlowFixMe[prop-missing]
            miscFeesCorporate.total?.before_taxes ||
            0;

          return acc;
        },
        { count: 0, priceBeforeTaxes: 0 },
      ),
  );

/**
 * Search if any product has error on language for IDN.
 */
export const IDNErrorRegexp: RegExp = /^lang: mandatory for/;
export function hasIDNError(productErrors: Array<ProductError>): boolean {
  return (
    productErrors.filter(error => IDNErrorRegexp.test(error.description))
      .length > 0
  );
}

export const selectHasDomainCreateIDNErrors: (state: FullState) => boolean =
  createSelector([selectProducts, state => state], (products, state) => {
    return products
      .map(({ id: productId }) =>
        hasIDNError(selectOneProductErrors(state, { productId })),
      )
      .some(Boolean);
  });

export type SelectCorporateMiscFeesByKind = (
  state: FullState,
) => CorporateMiscFeesByKind;
export const selectCorporateMiscFeesByKind: SelectCorporateMiscFeesByKind =
  createStructuredSelector({
    DomainCreate: selectCorporateMiscFeesFactory({
      productType: 'domain',
      process: 'create',
    }),
    DomainRenew: selectCorporateMiscFeesFactory({
      productType: 'domain',
      process: 'renew',
    }),
    DomainRestore: selectCorporateMiscFeesFactory({
      productType: 'domain',
      process: 'restore',
    }),
    DomainTransfer: selectCorporateMiscFeesFactory({
      productType: 'domain',
      process: 'transfer',
    }),
  });

export const selectMoreThan1YearDuration: (
  state: FullState,
  payload: ProductPayload,
) => boolean = createSelector(
  [(_, props) => props],
  ({ duration }) => !!duration && duration > 1,
);

export const selectProductPrice: (
  state: FullState,
  { productId: number | string },
) => ?ProductPrice = createSelector(
  [selectOneProductById],
  product => product?.prices?.[0],
);

export const selectNameserversUrl: (state: FullState, fqdn: string) => string =
  createSelector([selectAdminURL, (_, fqdn) => fqdn], (adminUrl, fqdn) =>
    superpathjoin(adminUrl, 'domain', fqdn, 'nameservers'),
  );
